/**
 * The semen analysis she recorded on her vault samples, for the Home's
 * "Sperm freezing results" section.
 *
 * That section read two stores: `semenFluidAnalysis` test results (a filed lab
 * report) and the `spermiograms` rows on the cycle's phase (the treatment hub's
 * Analysis step). The Cryo Vault's add-specimen intake writes a third — the
 * spermiogram linked to each semen specimen, plus vitality and DNA
 * fragmentation as specimen-level metrics — and the Home never looked there.
 * So the "+" on "What you stored" led her to type concentration, motility and
 * morphology into the vault, and the results section a few blocks down kept
 * saying nothing had been recorded.
 *
 * Kept free of hooks so the rule can be asked in a test; the Home wraps it in
 * `useSpecimens`.
 */

import type { SpermAnalysisResults } from "./components/SpermAnalysisSkeletonCard";

/**
 * Only what this reader looks at. A real `Specimen` satisfies it; a test does
 * not have to hand over fifty columns to say "this vial's sample measured 48".
 */
export type SpecimenAnalysisRead = {
  type: string;
  cycleId?: string | null;
  collectedAt?: string | null;
  createdAt?: string | null;
  vitality?: number | null;
  dnaFragmentation?: number | null;
  spermiogram?: {
    semenVolume?: number | null;
    volumeUnit?: string | null;
    spermConcentration?: number | null;
    progressiveMotility?: number | null;
    normalMorphology?: number | null;
  } | null;
};

const figure = (value: unknown): string | undefined =>
  typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : undefined;

const recordedAt = (specimen: SpecimenAnalysisRead): string =>
  String(specimen.collectedAt ?? specimen.createdAt ?? "");

/**
 * The analysis behind her stored sperm, as the results card wants it.
 *
 * The samples of the cycle that ended are preferred, so a Home about that
 * cycle reads that cycle's lab figures; a preservation-only sample carries no
 * cycle, so without a match every semen specimen she holds is considered. The
 * most recently collected vial speaks for the rest: the vials of one intake
 * share one sample and one spermiogram, and a newer collection is the newer
 * reading.
 *
 * Values come back as bare numbers where the card supplies the unit itself;
 * the volume keeps its unit when the record measured in anything but
 * millilitres — the same rule the phase reader follows.
 */
export function semenAnalysisResultsFromSpecimens(
  specimens: readonly SpecimenAnalysisRead[] | undefined | null,
  cycleId?: string | null,
): SpermAnalysisResults | null {
  const semen = (specimens ?? []).filter((s) => s.type === "semen");
  if (semen.length === 0) return null;
  const ofCycle = cycleId ? semen.filter((s) => s.cycleId === cycleId) : [];
  const pool = ofCycle.length > 0 ? ofCycle : semen;
  const newest = [...pool].sort((left, right) =>
    recordedAt(right).localeCompare(recordedAt(left)),
  )[0]!;

  const mapped: SpermAnalysisResults = {};
  const sperm = newest.spermiogram;
  const volume = figure(sperm?.semenVolume);
  if (volume) {
    const unit = sperm?.volumeUnit?.trim();
    mapped.volume =
      unit && unit.toLowerCase() !== "ml" ? `${volume} ${unit}` : volume;
  }
  const concentration = figure(sperm?.spermConcentration);
  if (concentration) mapped.concentration = concentration;
  const progressive = figure(sperm?.progressiveMotility);
  if (progressive) mapped.progressiveMotility = progressive;
  const morphology = figure(sperm?.normalMorphology);
  if (morphology) mapped.normalMorphology = morphology;
  const vitality = figure(newest.vitality);
  if (vitality) mapped.vitality = vitality;
  const dnaFragmentation = figure(newest.dnaFragmentation);
  if (dnaFragmentation) mapped.dnaFragmentation = dnaFragmentation;

  return Object.keys(mapped).length > 0 ? mapped : null;
}
